import { apiClient } from './client'
import type { ApiResponse } from './client'
import type {
    OutboundWebhookSubscriptionViewDto,
    OutboundWebhookSubscriptionCreateDto,
    OutboundWebhookSubscriptionUpdateDto,
    OutboundWebhookSecretRevealDto,
    OutboundWebhookDeliveryListResponse,
    OutboundWebhookResendAllResultDto,
    OutboundWebhookDeliveryViewDto,
    OutboundWebhookTestResultDto,
} from '@/types/outboundWebhooks'

export interface DeliveryQuery {
    subscriptionId?: string
    status?: string
    workspaceId?: string
    limit?: number
}

const deliveryQueryString = (query: DeliveryQuery): string => {
    const params = new URLSearchParams()
    if (query.subscriptionId) params.set('subscriptionId', query.subscriptionId)
    if (query.status) params.set('status', query.status)
    if (query.workspaceId) params.set('workspaceId', query.workspaceId)
    if (query.limit) params.set('limit', String(query.limit))
    const qs = params.toString()
    return qs ? `?${qs}` : ''
}

export const outboundWebhooksApi = {
    getSubscriptions: (clientCompanyId: string): Promise<ApiResponse<OutboundWebhookSubscriptionViewDto[]>> =>
        apiClient.get(`/api/ClientCompanies/${clientCompanyId}/OutboundWebhooks`),

    // The 201 body carries the plaintext signing secret. It is the only time it exists outside a
    // send, so the caller must surface it immediately and never stash it.
    createSubscription: (
        clientCompanyId: string,
        dto: OutboundWebhookSubscriptionCreateDto,
    ): Promise<ApiResponse<OutboundWebhookSecretRevealDto>> =>
        apiClient.post(`/api/ClientCompanies/${clientCompanyId}/OutboundWebhooks`, dto),

    updateSubscription: (
        clientCompanyId: string,
        id: string,
        dto: OutboundWebhookSubscriptionUpdateDto,
    ): Promise<ApiResponse<OutboundWebhookSubscriptionViewDto>> =>
        apiClient.put(`/api/ClientCompanies/${clientCompanyId}/OutboundWebhooks/${id}`, dto),

    deleteSubscription: (clientCompanyId: string, id: string): Promise<ApiResponse<null>> =>
        apiClient.delete(`/api/ClientCompanies/${clientCompanyId}/OutboundWebhooks/${id}`),

    // Same one-time-reveal contract as create.
    rotateSecret: (clientCompanyId: string, id: string): Promise<ApiResponse<OutboundWebhookSecretRevealDto>> =>
        apiClient.post(`/api/ClientCompanies/${clientCompanyId}/OutboundWebhooks/${id}/Secret`, {}),

    sendTest: (clientCompanyId: string, id: string): Promise<ApiResponse<OutboundWebhookTestResultDto>> =>
        apiClient.post(`/api/ClientCompanies/${clientCompanyId}/OutboundWebhooks/${id}/Test`, {}),

    getDeliveries: (
        clientCompanyId: string,
        query: DeliveryQuery = {},
    ): Promise<ApiResponse<OutboundWebhookDeliveryListResponse>> =>
        apiClient.get(`/api/ClientCompanies/${clientCompanyId}/OutboundWebhooks/Deliveries${deliveryQueryString(query)}`),

    // 201 with the replacement row, or 409 when the row cannot be resent (still in progress, already
    // superseded). The refusal reason is in the error body.
    resendDelivery: (
        clientCompanyId: string,
        deliveryId: string,
    ): Promise<ApiResponse<OutboundWebhookDeliveryViewDto>> =>
        apiClient.post(`/api/ClientCompanies/${clientCompanyId}/OutboundWebhooks/Deliveries/${deliveryId}/Resend`, {}),

    resendAllFailed: (
        clientCompanyId: string,
        subscriptionId?: string,
    ): Promise<ApiResponse<OutboundWebhookResendAllResultDto>> =>
        apiClient.post(
            `/api/ClientCompanies/${clientCompanyId}/OutboundWebhooks/Deliveries/Resend${subscriptionId ? `?subscriptionId=${encodeURIComponent(subscriptionId)}` : ''}`,
            {},
        ),
}
